import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * LinkUserModal - Link a contact to a registered user account
 * Uses gold theme from BaseModal system
 */
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import BaseModal, { Input as BaseInput, FormLabel, GhostButton, } from './shared/BaseModal';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import { FaUser, FaSearch, FaLink, FaUnlink, FaUserShield, FaSpinner } from 'react-icons/fa';
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;
const InfoLabel = styled.span `
  font-size: 14px;
  color: var(--text-tertiary);
`;
const InfoValue = styled.span `
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
`;
const LinkedUserBox = styled.div `
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;
const LinkedUserHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;
const LinkedUserIcon = styled.div `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;
const LinkedUserInfo = styled.div `
  flex: 1;
`;
const LinkedUserEmail = styled.div `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;
const LinkedUserRole = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  gap: 6px;
`;
const SearchContainer = styled.div `
  position: relative;
  margin-bottom: 16px;
`;
const SearchInput = styled(BaseInput) `
  padding-left: 40px;
`;
const SearchIcon = styled.div `
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
`;
const UserList = styled.div `
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 24px;
`;
const UserItem = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props => props.isSelected ? 'rgba(184, 134, 11, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.isSelected ? 'var(--accent-gold)' : 'transparent'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-tertiary);
  }
`;
const UserAvatar = styled.div `
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => {
    switch (props.role) {
        case 'SUPER_ADMIN': return '#7c3aed';
        case 'PLATFORM_ADMIN': return '#2563eb';
        case 'SALES_MANAGER': return '#0891b2';
        case 'DEALER': return '#059669';
        default: return '#6b7280';
    }
}};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;
const UserDetails = styled.div `
  flex: 1;
`;
const UserEmail = styled.div `
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;
const UserMeta = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  gap: 8px;
`;
const RoleBadge = styled.span `
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => {
    switch (props.role) {
        case 'SUPER_ADMIN': return 'rgba(124, 58, 237, 0.15)';
        case 'PLATFORM_ADMIN': return 'rgba(37, 99, 235, 0.15)';
        case 'SALES_MANAGER': return 'rgba(8, 145, 178, 0.15)';
        case 'DEALER': return 'rgba(5, 150, 105, 0.15)';
        default: return 'rgba(107, 114, 128, 0.15)';
    }
}};
  color: ${props => {
    switch (props.role) {
        case 'SUPER_ADMIN': return '#7c3aed';
        case 'PLATFORM_ADMIN': return '#2563eb';
        case 'SALES_MANAGER': return '#0891b2';
        case 'DEALER': return '#059669';
        default: return '#6b7280';
    }
}};
`;
const StatusBadge = styled.span `
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => props.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  color: ${props => props.status === 'active' ? '#059669' : '#dc2626'};
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 40px 20px;
  color: var(--text-tertiary);
`;
const LoadingState = styled.div `
  text-align: center;
  padding: 40px 20px;
  color: var(--text-tertiary);

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const ActionButton = styled.button `
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'link' ? 'var(--accent-gold)' : '#DC2626'};
  background: ${props => props.variant === 'link'
    ? 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-gold-dark) 100%)'
    : '#DC2626'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.variant === 'link'
    ? 'rgba(184, 134, 11, 0.3)'
    : 'rgba(220, 38, 38, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;
const LinkUserModal = ({ contact, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    // Load users on mount
    const loadUsers = useCallback(async () => {
        setSearching(true);
        try {
            const response = await apiClient.get('users-get-all');
            if (response.ok && response.data?.users) {
                setUsers(response.data.users);
            }
        }
        catch (error) {
            console.error('Failed to load users:', error);
        }
        finally {
            setSearching(false);
        }
    }, []);
    useEffect(() => {
        loadUsers();
    }, [loadUsers]);
    // Filter users based on search
    const filteredUsers = users.filter(user => {
        if (!searchTerm)
            return true;
        const search = searchTerm.toLowerCase();
        return user.email.toLowerCase().includes(search) ||
            user.role.toLowerCase().includes(search);
    });
    const handleLinkUser = async () => {
        if (!selectedUser)
            return;
        setLoading(true);
        try {
            const response = await apiClient.put(`contacts-update/${contact.id}`, {
                userId: selectedUser.id,
                userEmail: selectedUser.email,
                userRole: selectedUser.role
            });
            if (response.ok) {
                notify.success('Success', `Linked to user: ${selectedUser.email}`);
                onClose(true);
            }
            else {
                const errorData = response.data;
                notify.error('Error', errorData?.error || 'Failed to link user');
            }
        }
        catch (error) {
            console.error('Failed to link user:', error);
            notify.error('Error', 'Failed to link user');
        }
        finally {
            setLoading(false);
        }
    };
    const handleUnlinkUser = async () => {
        setLoading(true);
        try {
            const response = await apiClient.put(`contacts-update/${contact.id}`, {
                userId: null,
                userEmail: null,
                userRole: null
            });
            if (response.ok) {
                notify.success('Success', 'User account unlinked');
                onClose(true);
            }
            else {
                const errorData = response.data;
                notify.error('Error', errorData?.error || 'Failed to unlink user');
            }
        }
        catch (error) {
            console.error('Failed to unlink user:', error);
            notify.error('Error', 'Failed to unlink user');
        }
        finally {
            setLoading(false);
        }
    };
    const isLinked = !!contact.userId;
    return (_jsxs(BaseModal, { isOpen: true, onClose: () => onClose(false), title: "Link User Account", titleAccent: "User Account", size: "md", children: [_jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Company" }), _jsx(InfoValue, { children: contact.companyName })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Contact Person" }), _jsx(InfoValue, { children: contact.contactPerson })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Email" }), _jsx(InfoValue, { children: contact.email })] })] }), isLinked && (_jsx(LinkedUserBox, { children: _jsxs(LinkedUserHeader, { children: [_jsx(LinkedUserIcon, { children: _jsx(FaUser, {}) }), _jsxs(LinkedUserInfo, { children: [_jsx(LinkedUserEmail, { children: contact.userEmail }), _jsxs(LinkedUserRole, { children: [_jsx(FaUserShield, { size: 10 }), contact.userRole] })] }), _jsxs(ActionButton, { variant: "unlink", onClick: handleUnlinkUser, disabled: loading, children: [_jsx(FaUnlink, {}), " Unlink"] })] }) })), !isLinked && (_jsxs(_Fragment, { children: [_jsx(FormLabel, { children: "Search Registered Users" }), _jsxs(SearchContainer, { children: [_jsx(SearchIcon, { children: _jsx(FaSearch, {}) }), _jsx(SearchInput, { placeholder: "Search by email...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsx(UserList, { children: searching ? (_jsxs(LoadingState, { children: [_jsx(FaSpinner, { size: 24 }), _jsx("p", { children: "Loading users..." })] })) : filteredUsers.length === 0 ? (_jsxs(EmptyState, { children: [_jsx(FaUser, { size: 32, style: { marginBottom: 12, opacity: 0.5 } }), _jsx("p", { children: searchTerm ? 'No users match your search' : 'No registered users found' })] })) : (filteredUsers.map(user => (_jsxs(UserItem, { isSelected: selectedUser?.id === user.id, onClick: () => setSelectedUser(user), children: [_jsx(UserAvatar, { role: user.role, children: user.email.charAt(0).toUpperCase() }), _jsxs(UserDetails, { children: [_jsx(UserEmail, { children: user.email }), _jsxs(UserMeta, { children: [_jsx(RoleBadge, { role: user.role, children: user.role.replace('_', ' ') }), _jsx(StatusBadge, { status: user.status, children: user.status })] })] })] }, user.id)))) })] })), _jsxs(ButtonGroup, { children: [_jsx(GhostButton, { onClick: () => onClose(false), disabled: loading, children: "Cancel" }), !isLinked && (_jsxs(ActionButton, { variant: "link", onClick: handleLinkUser, disabled: loading || !selectedUser, children: [_jsx(FaLink, {}), " Link User"] }))] })] }));
};
export default LinkUserModal;
